export interface VideoFrameOptions {
    /** Width of the picture in pixels; the height follows the video's shape. */
    width?: number;
    /** The moment to capture. Shorter videos are captured at their middle. */
    atSec?: number;
    /**
     * Ask the server for permission to read the pixels. Needed for a video on
     * another origin; one on this page's origin (or a blob URL) needs no asking.
     */
    crossOrigin?: boolean;
    timeoutMs?: number;
}

/**
 * One frame of a video as a small JPEG. Only as much of the video as the frame
 * needs is loaded, and the video is let go as soon as the frame is read.
 */
export const captureVideoFrame = (url: string, options: VideoFrameOptions = {}): Promise<Blob> => {
    const { width = 192, atSec = 0.5, crossOrigin = false, timeoutMs = 20000 } = options;
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        let settled = false;
        let drawing = false;

        const finish = (error: Error | null, blob?: Blob | null) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.removeAttribute('src');
            video.load();
            if (error || !blob) reject(error ?? new Error('The frame could not be encoded.'));
            else resolve(blob);
        };
        const timer = setTimeout(() => finish(Object.assign(new Error('The video took too long to load.'), { name: 'TimeoutError' })), timeoutMs);

        const draw = () => {
            if (settled || drawing || video.seeking || video.readyState < 2 || !video.videoWidth) return;
            drawing = true;
            try {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = Math.max(1, Math.round(width * video.videoHeight / video.videoWidth));
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Drawing is not available in this browser.');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => finish(null, blob), 'image/jpeg', 0.72);
            } catch (e) {
                // Reading pixels of a video the server did not share throws here
                finish(e instanceof Error ? e : new Error('The frame could not be read.'));
            }
        };

        video.muted = true;
        video.playsInline = true;
        // Only the metadata up front; the seek below then loads just the data around one frame,
        // so a video read over the network costs a small part of the file.
        video.preload = 'metadata';
        if (crossOrigin) video.crossOrigin = 'anonymous';
        video.onloadedmetadata = () => {
            // Some recordings report no length until played through; a short seek still lands on a frame.
            const duration = Number.isFinite(video.duration) ? video.duration : 0;
            video.currentTime = duration > 0 ? Math.min(atSec, duration / 2) : Math.min(atSec, 0.1);
        };
        video.onseeked = draw;
        video.onloadeddata = draw;
        video.oncanplay = draw;
        video.onerror = () => finish(new Error('The video could not be read.'));
        video.src = url;
    });
};
